import React, { useEffect, useState } from 'react';
import { Card, Table, DatePicker, Button, Typography, message, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

const { Title } = Typography;

interface AttendanceRow {
  Phong: string;
  Lop: string;
  SiSo: number;
  VangTiet1: number;
  QrCreatedAt: string;
  GiangVien: string;
}

interface AttendanceData {
  Sáng: AttendanceRow[];
  Chiều: AttendanceRow[];
  Tối: AttendanceRow[];
  totals: {
    Sáng: { SiSo: number; VangTiet1: number };
    Chiều: { SiSo: number; VangTiet1: number };
    Tối: { SiSo: number; VangTiet1: number };
  };
}

const columns = [
  { title: 'Phòng', dataIndex: 'Phong', key: 'Phong' },
  { title: 'Lớp', dataIndex: 'Lop', key: 'Lop' },
  { title: 'Sĩ số', dataIndex: 'SiSo', key: 'SiSo' },
  { title: 'Vắng Tiết 1', dataIndex: 'VangTiet1', key: 'VangTiet1' },
  {
    title: 'Thời gian tạo QR',
    dataIndex: 'QrCreatedAt',
    key: 'QrCreatedAt',
    render: (value: string) => value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '',
  },
  { title: 'Giảng viên', dataIndex: 'GiangVien', key: 'GiangVien' },
];

const AttendanceStartStatisticPage: React.FC = () => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AttendanceData | null>(null);

  const fetchData = async (selectedDate: Dayjs) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/statistics/attendance-start?mode=date&date=${selectedDate.format('YYYY-MM-DD')}`);
      const json = await res.json();
      if (json.success) {
        // Log cấu trúc dữ liệu để debug
        // eslint-disable-next-line no-console
        console.log('[DEBUG] Dữ liệu thống kê điểm danh đầu giờ:', json.data);
        setData(json.data);
      } else {
        setData(null);
        message.error(json.message || 'Lỗi lấy dữ liệu');
      }
    } catch (err) {
      setData(null);
      message.error('Lỗi kết nối máy chủ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(date);
    // eslint-disable-next-line
  }, []);

  const handleDateChange = (d: Dayjs | null) => {
    if (d) setDate(d);
  };

  const handleFilter = () => {
    fetchData(date);
  };

  return (
    <Card style={{ margin: 24 }}>
      <Title level={3}>Thống kê điểm danh đầu giờ</Title>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Theo ngày</span>
        <DatePicker value={date} onChange={handleDateChange} allowClear={false} format="YYYY-MM-DD" />
        <Button type="primary" onClick={handleFilter}>Lọc</Button>
      </div>
      <Spin spinning={loading} tip="Đang tải dữ liệu...">
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ marginBottom: 8 }}>Sáng</Title>
            <Table
              columns={columns}
              dataSource={data?.Sáng || []}
              rowKey={(r) => typeof r === 'object' && r !== null ? (r.Phong + r.Lop) : ''}
              pagination={false}
              locale={{ emptyText: 'Trống' }}
              summary={() => data?.totals?.Sáng ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>Tổng</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{data.totals.Sáng.SiSo}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{data.totals.Sáng.VangTiet1}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
            />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ marginBottom: 8 }}>Chiều</Title>
            <Table
              columns={columns}
              dataSource={data?.Chiều || []}
              rowKey={(r) => typeof r === 'object' && r !== null ? (r.Phong + r.Lop) : ''}
              pagination={false}
              locale={{ emptyText: 'Trống' }}
              summary={() => data?.totals?.Chiều ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>Tổng</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{data.totals.Chiều.SiSo}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{data.totals.Chiều.VangTiet1}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
            />
        </div>
        <div>
          <Title level={4} style={{ marginBottom: 8 }}>Tối</Title>
            <Table
              columns={columns}
              dataSource={data?.Tối || []}
              rowKey={(r) => typeof r === 'object' && r !== null ? (r.Phong + r.Lop) : ''}
              pagination={false}
              locale={{ emptyText: 'Trống' }}
              summary={() => data?.totals?.Tối ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>Tổng</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{data.totals.Tối.SiSo}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{data.totals.Tối.VangTiet1}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
            />
        </div>
      </Spin>
    </Card>
  );
};

export default AttendanceStartStatisticPage;
